// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — pierSfx.js   ·   THE SOUNDS A BUILDING GAME MAKES
// ────────────────────────────────────────────────────────────────────
// (2026-09-15) `grep` for Audio, mp3, ogg or wav across the whole app
// returned nothing. A chair went down in silence, a barn was collected
// in silence, a refusal was a line of 11px text at the top of the
// screen. Every cozy building game caP'n named this against makes a
// noise when you put a thing down, and the noise is half of why placing
// things is satisfying.
//
// NO ASSETS. Every sound here is two or three oscillators and an
// envelope — a WebAudio synth, under a kilobyte, no CDN, no CORS, no
// loading. When real recordings exist they replace the bodies of these
// functions and nothing else changes (the seam is the NAMES).
//
// ⚑ THE BROWSER LOCKS AUDIO UNTIL A GESTURE. The context is created on
// the first call, which is always inside a click or a keypress, so it
// starts unlocked. Every call is fire-and-forget and never throws — a
// sound must never be able to fail a placement.
// ⚑ MUTE is one localStorage key, read on every call, so a toggle
// anywhere takes effect without a reload.
// ════════════════════════════════════════════════════════════════════

const MUTE_KEY = "ie_sfx_muted";
let ctx = null;

const muted = () => { try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; } };
export const isMuted = muted;
export function setMuted(v) { try { localStorage.setItem(MUTE_KEY, v ? "1" : "0"); } catch { /* private mode */ } }
export function toggleMuted() { const v = !muted(); setMuted(v); return v; }

function ac() {
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  } catch { ctx = null; }
  return ctx;
}

// one voice: type, frequency glide, gain envelope. Everything below is
// built from these.
function voice(c, { type = "sine", f0 = 440, f1 = f0, t0 = 0, a = 0.005, d = 0.12, g = 0.2, curve = "exp" }) {
  const o = c.createOscillator();
  const v = c.createGain();
  const now = c.currentTime + t0;
  o.type = type;
  o.frequency.setValueAtTime(f0, now);
  if (f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), now + d);
  v.gain.setValueAtTime(0.0001, now);
  v.gain.linearRampToValueAtTime(g, now + a);
  if (curve === "exp") v.gain.exponentialRampToValueAtTime(0.0001, now + a + d);
  else v.gain.linearRampToValueAtTime(0.0001, now + a + d);
  o.connect(v).connect(c.destination);
  o.start(now);
  o.stop(now + a + d + 0.02);
}

// a short burst of filtered noise — the "thock" of a thing set down
function thud(c, { t0 = 0, d = 0.09, g = 0.25, f = 900 } = {}) {
  const n = Math.floor(c.sampleRate * d);
  const buf = c.createBuffer(1, n, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < n; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / n) ** 2;
  const src = c.createBufferSource();
  src.buffer = buf;
  const lp = c.createBiquadFilter();
  lp.type = "lowpass"; lp.frequency.value = f;
  const v = c.createGain();
  v.gain.value = g;
  src.connect(lp).connect(v).connect(c.destination);
  src.start(c.currentTime + t0);
}

const play = (fn) => {
  if (muted()) return;
  const c = ac();
  if (!c) return;
  try { if (c.state === "suspended") c.resume(); fn(c); } catch { /* never let a sound fail an action */ }
};

// ── THE VOCABULARY ──────────────────────────────────────────────────
export const sfx = {
  // a piece set on the ground: a thock and a low wooden note
  place:   () => play((c) => { thud(c, { g: 0.22, f: 700 }); voice(c, { type: "triangle", f0: 220, f1: 160, d: 0.14, g: 0.12 }); }),
  // balanced on top of something: the thock, higher, and a little tick
  stack:   () => play((c) => { thud(c, { g: 0.16, f: 1400, d: 0.06 }); voice(c, { type: "triangle", f0: 520, f1: 420, d: 0.1, g: 0.09 }); }),
  // lifted back into the shed: the note in reverse
  pickup:  () => play((c) => { voice(c, { type: "triangle", f0: 180, f1: 320, d: 0.12, g: 0.1 }); }),
  // a turn: one dry click
  turn:    () => play((c) => { thud(c, { g: 0.12, f: 2400, d: 0.03 }); }),
  // Dawgs collected: a rising two-note chime, brighter the bigger the take
  harvest: (amount = 0) => play((c) => {
    const up = Math.min(1, Math.log10(1 + (amount || 0)) / 3);
    voice(c, { type: "sine", f0: 660 + up * 220, d: 0.16, g: 0.16 });
    voice(c, { type: "sine", f0: 990 + up * 330, t0: 0.09, d: 0.22, g: 0.14 });
    voice(c, { type: "sine", f0: 1320 + up * 440, t0: 0.18, d: 0.3, g: 0.08 });
  }),
  // a refusal: a soft low buzz, never harsh — refusing is an answer
  deny:    () => play((c) => { voice(c, { type: "square", f0: 140, f1: 110, d: 0.13, g: 0.05, curve: "lin" }); }),
  // a purchase: the till
  buy:     () => play((c) => { voice(c, { type: "sine", f0: 880, d: 0.08, g: 0.12 }); voice(c, { type: "sine", f0: 1174, t0: 0.07, d: 0.16, g: 0.1 }); }),
  // a panel opening or closing: barely there
  ui:      () => play((c) => { voice(c, { type: "sine", f0: 520, f1: 480, d: 0.05, g: 0.05 }); }),
  // a door: a low sweep
  door:    () => play((c) => { voice(c, { type: "sine", f0: 160, f1: 90, d: 0.35, g: 0.12 }); thud(c, { g: 0.1, f: 400, d: 0.2 }); }),
  // undo: the pickup note, quieter
  undo:    () => play((c) => { voice(c, { type: "triangle", f0: 300, f1: 200, d: 0.1, g: 0.07 }); }),
};

export default sfx;
